/**
 * iter73 — Read the global Ready-Made sale config from GET /api/site/sale.
 *
 * Caches in module scope so every mount reuses the same promise + result
 * (typical grid page mounts a dozen cards — we do a single request).
 *
 * Returns:
 *   { active: bool, pct: number, label: string, loaded: bool }
 *   - `loaded=false` on first render before the network settles; UI should
 *     render the un-discounted price during this brief window.
 */
import { useEffect, useState } from "react";
import { getReadyMadeSalePublic } from "../lib/api";

let _cache = null;
let _pending = null;

async function _load() {
  if (_cache) return _cache;
  if (!_pending) {
    _pending = getReadyMadeSalePublic()
      .then((d) => {
        _cache = {
          active: Boolean(d?.active),
          pct: Number(d?.pct) || 0,
          label: String(d?.label || ""),
          endsAt: d?.ends_at || null,
          loaded: true,
        };
        return _cache;
      })
      .catch(() => {
        // Fail silently — a stale/inactive sale is a valid default.
        _cache = { active: false, pct: 0, label: "", endsAt: null, loaded: true };
        return _cache;
      });
  }
  return _pending;
}

export function useReadyMadeSale() {
  const [state, setState] = useState(
    _cache || { active: false, pct: 0, label: "", endsAt: null, loaded: false }
  );
  useEffect(() => {
    if (_cache) return;
    let ok = true;
    _load().then((c) => { if (ok) setState(c); });
    return () => { ok = false; };
  }, []);
  return state;
}

// Bust the cache — used by the admin toggle UI so the operator sees their
// change reflected immediately without a hard-reload.
export function invalidateReadyMadeSale() {
  _cache = null;
  _pending = null;
}

/** Apply sale to a base price. Returns { discounted, showSlash }. */
export function applyReadyMadeSale(basePrice, sale) {
  const price = Number(basePrice) || 0;
  if (!sale?.active || !sale.pct) return { discounted: price, showSlash: false };
  const discounted = Math.round(price * (1 - sale.pct / 100) * 100) / 100;
  return { discounted, showSlash: true };
}
